import ReactECharts from "echarts-for-react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import { Panel } from "primereact/panel";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import NoInfo from "../../components/NoInfo";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { getSubItemsByItemNo } from "../../utility/service/api-service";
import { queryCompanyTagList } from "../../utility/service/company-service";
import { getCBAMSubItemsByItemNo } from "../../utility/service/companyOffering-service";
import { queryIndustryList } from "../../utility/service/register-service";
import { getDashboardData } from "../../utility/service/statistics-service";
import { checkDpSuccess, getFormErrorMessage, setformate } from "../../utility/service/tool-service";
import CusChart from "./CusChar";

const UserDashboardIndex = () => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [defaultValues, setDefaultValues] = useState({});
  const op = useRef(null);
  const buttonRef = useRef(null);
  const [filters] = useState({
    countdown: {
      operator: FilterOperator.OR,
      constraints: [{ value: null, matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO }],
    },
  });

  // 全域圖表/表格切換狀態
  const [globalViewMode, setGlobalViewMode] = useState(true); // true: 圖表, false: 表格

  // 條件選單
  const [periodType, setPeriodType] = useState("week");
  const [typeList, setTypeList] = useState([]);
  const [industryList, setIndustryList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [effectiveTypeList, setEffectiveTypeList] = useState([]);
  const [membershipLevelList, setMembershipLevelList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [localeList, setLocaleList] = useState([]);
  const [tagList, setTagList] = useState([]);

  // 統計資料
  const [totalCompanyList, setTotalCompanyList] = useState([]);
  const [totalUserList, setTotalUserList] = useState([]);
  const [totalStatusList, setTotalStatusList] = useState([]);
  const [totalEffectiveTypeList, setTotalEffectiveTypeList] = useState([]);
  const [totalMembershipLevelList, setTotalMembershipLevelList] = useState([]);
  const [totalIndustryList, setTotalIndustryList] = useState([]);
  const [totalCountryList, setTotalCountryList] = useState([]);
  const [totalLocaleList, setTotalLocaleList] = useState([]);
  const [registrationTrend, setRegistrationTrend] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [totalTagList, setTotalTagList] = useState([]);
  const [countdownList, setCountdownList] = useState([]);
  const [selectedType, setSelectedType] = useState('A');
  const [typeOptions, setTypeOptions] = useState([]);

  const {
    getValues,
    setValue,
    watch,
    control,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    //錯誤驗證時機
    mode: "all",
  });

  const axios_getDashboardData = (reqBody = {}) => {
    checkLoading(loading);
    getDashboardData(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data?.RespBody;
          setTotalUserList(data?.userTotalList);
          setTotalCompanyList(data?.companyTotalList);
          setTotalStatusList(data?.statusList);
          setTotalEffectiveTypeList(data?.effectiveTypeList);
          setTotalMembershipLevelList(data?.membershipLevelList);
          setTotalIndustryList(data?.industryList);
          setTotalCountryList(data?.countryList);
          setTotalLocaleList(data?.localeList);
          setRegistrationTrend(data?.filterCompanyList);
          setTotalTagList(data?.tagList);
          setCountdownList(data?.countdownList);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  useEffect(() => {
    if (
      typeList.length &&
      statusList.length &&
      effectiveTypeList.length &&
      membershipLevelList.length &&
      localeList.length
    ) {
      const tmp = {
        typeList: typeList.filter((o) => o.subitemNo !== "EsgManager").map((o) => o.subitemNo),
        statusList: statusList.map((o) => o.subitemNo),
        effectiveTypeList: [],
        membershipLevelList: [],
        industryList: [],
        countryList: [],
        localeList: [],
        periodType: "week",
      };
      setDefaultValues(tmp);
      reset(tmp);
      // 使用空的查詢條件進行自動查詢
      axios_getDashboardData({
        typeList: typeList.filter((o) => o.subitemNo !== "EsgManager").map((o) => o.subitemNo),
        statusList: statusList.map((o) => o.subitemNo),
        effectiveTypeList: [],
        membershipLevelList: [],
        localeList: [],
      });
    }
  }, [typeList, statusList, effectiveTypeList, membershipLevelList, localeList, reset]);

  useEffect(() => {
    axios_queryIndustryList();
    axios_getSubItemsByItemNo("ACCT_TYPE", 11).then(setTypeList);
    axios_getSubItemsByItemNo("COMPANY_STATUS", 23).then(setStatusList);
    axios_getSubItemsByItemNo("EFFECTIVE_TYPE", 17).then(setEffectiveTypeList);
    axios_getSubItemsByItemNo("COMPANY_LEVEL", 35).then(setMembershipLevelList);
    axios_getCBAMSubItemsByItemNo("CBAM_COUNTRIES", 0).then(setCountryList);
    axios_getSubItemsByItemNo("LOCALE", 36).then(setLocaleList);
    axios_queryCompanyTagList();
  }, []);

  useEffect(() => {
    const trendData = registrationTrend;

    if (trendData?.series?.netChange && trendData.series.netChange.length > 0) {

      const newTypeOptions = trendData.series.netChange.map(s => ({
        label: s.labelName,
        value: s.label,
      }));

      setTypeOptions(newTypeOptions);

      const isSelectedTypeValid = newTypeOptions.some(o => o.value === selectedType);

      if (!isSelectedTypeValid && newTypeOptions.length > 0) {
        const defaultType = newTypeOptions.find(o => o.value === 'A')?.value
          || newTypeOptions[0].value;

        setSelectedType(defaultType);
      } else if (newTypeOptions.length === 0) {
        setSelectedType('');
      }

    } else if (Object.keys(trendData).length === 0) {
      setTypeOptions([]);
      setSelectedType('');
    }
  }, [registrationTrend, selectedType]);

  // 監聽視窗大小變化，用於響應式圖表調整
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formValues = watch();

  //取得行業別下拉清單
  const axios_queryIndustryList = () => {
    queryIndustryList().then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody?.industryList;
        setIndustryList(data);
      }
    });
  };

  const axios_getSubItemsByItemNo = (itemNo, orderNo) => {
    return getSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const axios_queryCompanyTagList = () => {
    queryCompanyTagList().then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody?.tagList;
        setTagList(data);
      }
    });
  };

  const getLabels = (values, options, optionValue = "subitemNo", optionLabel = "subitemName") => {
    if (!values) return [];
    return options.filter((opt) => values.includes(opt[optionValue])).map((opt) => opt[optionLabel]);
  };

  const onSubmit = (data) => {
    let reqBody = {
      typeList: data.typeList,
      industryList: data.industryList,
      statusList: data.statusList,
      effectiveTypeList: data.effectiveTypeList,
      membershipLevelList: data.membershipLevelList,
      countryList: data.countryList,
      localeList: data.localeList,
      periodType: periodType,
      tagList: data?.tagList,
    };
    if (data.createStarTime) {
      reqBody.createStarTime = setformate(data.createStarTime, "YYYY/MM/DD");
    }
    if (data.createEndTime) {
      reqBody.createEndTime = setformate(data.createEndTime, "YYYY/MM/DD");
    }
    if (data.effectiveStartDate) {
      reqBody.effectiveStartDate = setformate(data.effectiveStartDate, "YYYY/MM/DD");
    }
    if (data.effectiveEndDate) {
      reqBody.effectiveEndDate = setformate(data.effectiveEndDate, "YYYY/MM/DD");
    }
    axios_getDashboardData(reqBody);
  };

  // 生成動態標題
  const getDynamicHeader = () => {
    const hasFilters =
      formValues.typeList?.length > 0 ||
      formValues.industryList?.length > 0 ||
      formValues.statusList?.length > 0 ||
      formValues.effectiveTypeList?.length > 0 ||
      formValues.membershipLevelList?.length > 0 ||
      formValues.countryList?.length > 0 ||
      formValues.localeList?.length > 0 ||
      formValues.createStarTime ||
      formValues.createEndTime ||
      formValues.effectiveStartDate ||
      formValues.effectiveEndDate;

    if (!hasFilters) {
      return t("filterSearch");
    }

    const filterParts = [];
    const fullFilterParts = [];

    if (formValues.typeList?.length > 0) {
      const typeLabels = getLabels(formValues.typeList, typeList);
      filterParts.push(`${t("useType")}: ${typeLabels.join(", ")}`);
      fullFilterParts.push(`${t("useType")}: ${typeLabels.join(", ")}`);
    }
    if (formValues.industryList?.length > 0) {
      const industryLabels = getLabels(formValues.industryList, industryList, "industryCode", "industryName");
      const displayLabels =
        industryLabels.length > 5 ? industryLabels.slice(0, 5).join(", ") + "..." : industryLabels.join(", ");
      filterParts.push(`${t("industry")}: ${displayLabels}`);
      fullFilterParts.push(`${t("industry")}: ${industryLabels.join(", ")}`);
    }
    if (formValues.statusList?.length > 0) {
      const statusLabels = getLabels(formValues.statusList, statusList);
      filterParts.push(`${t("reviewStatusDistribution")}: ${statusLabels.join(", ")}`);
      fullFilterParts.push(`${t("reviewStatusDistribution")}: ${statusLabels.join(", ")}`);
    }
    if (formValues.effectiveTypeList?.length > 0) {
      const effectiveTypeLabels = getLabels(formValues.effectiveTypeList, effectiveTypeList);
      filterParts.push(`${t("authorizationType")}: ${effectiveTypeLabels.join(", ")}`);
      fullFilterParts.push(`${t("authorizationType")}: ${effectiveTypeLabels.join(", ")}`);
    }
    if (formValues.membershipLevelList?.length > 0) {
      const membershipLabels = getLabels(formValues.membershipLevelList, membershipLevelList);
      filterParts.push(`${t("membershipLevel")}: ${membershipLabels.join(", ")}`);
      fullFilterParts.push(`${t("membershipLevel")}: ${membershipLabels.join(", ")}`);
    }
    if (formValues.countryList?.length > 0) {
      const countryLabels = getLabels(formValues.countryList, countryList);
      const displayLabels =
        countryLabels.length > 5 ? countryLabels.slice(0, 5).join(", ") + "..." : countryLabels.join(", ");
      filterParts.push(`${t("country")}: ${displayLabels}`);
      fullFilterParts.push(`${t("country")}: ${countryLabels.join(", ")}`);
    }
    if (formValues.localeList?.length > 0) {
      const localeLabels = getLabels(formValues.localeList, localeList);
      filterParts.push(`${t("useLanguage")}: ${localeLabels.join(", ")}`);
      fullFilterParts.push(`${t("useLanguage")}: ${localeLabels.join(", ")}`);
    }
    if (formValues.createStarTime || formValues.createEndTime) {
      const dateRange = [];
      if (formValues.createStarTime) dateRange.push(setformate(formValues.createStarTime, "YYYY/MM/DD"));
      if (formValues.createEndTime) dateRange.push(setformate(formValues.createEndTime, "YYYY/MM/DD"));
      const dateStr = dateRange.join(" ~ ");
      filterParts.push(`${t("createDateTo")}: ${dateStr}`);
      fullFilterParts.push(`${t("createDateTo")}: ${dateStr}`);
    }
    if (formValues.effectiveStartDate || formValues.effectiveEndDate) {
      const dateRange = [];
      if (formValues.effectiveStartDate) dateRange.push(setformate(formValues.effectiveStartDate, "YYYY/MM/DD"));
      if (formValues.effectiveEndDate) dateRange.push(setformate(formValues.effectiveEndDate, "YYYY/MM/DD"));
      const dateStr = dateRange.join(" ~ ");
      filterParts.push(`${t("authorizationDate")}: ${dateStr}`);
      fullFilterParts.push(`${t("authorizationDate")}: ${dateStr}`);
    }

    return {
      display: `${t("currentFilters")}: ${filterParts.join(" | ")}`,
      full: `${t("currentFilters")}: ${fullFilterParts.join(" | ")}`,
    };
  };

  const headerContent = getDynamicHeader();
  const displayText = typeof headerContent === "object" ? headerContent.display : headerContent;
  const tooltipText = typeof headerContent === "object" ? headerContent.full : headerContent;

  const companyLevelNameTemplate = (rowData) => {
    if (!rowData) return null;
    const levelConfig = {
      "0": { color: "var(--primary-color)", label: t("formal") }, // 正式
      "1": { color: "var(--gray-400)", label: t("trial") }, // 試用
    };

    const config = levelConfig[rowData.effectiveType];
    if (!config) return null;

    return (
      <div className="user-avatar-button">
        <span style={{ color: config.color, fontSize: "1rem" }} className="contract-str">
          {config.label}
        </span>
      </div>
    );
  };

  const statusBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <span className={`p-tag p-tag-${rowData?.status === "0" ? "success" : "danger"}`}>
          {getStatusLabel(rowData?.status)}
        </span>
      );
    }
  };

  const getStatusLabel = (value) => {
    if (value === "D") return t("deactivate");
    const item = statusList?.find((item) => item.subitemNo === value);
    return item ? item.subitemName : null;
  };

  const companyTypeNameTemplate = (rowData) => {
    if (!rowData) return null;

    const typeColorMap = {
      A: "var(--green-500)", // 廠商
      B: "var(--blue-500)", // 顧問
      M: "var(--mint-500)", // 管理者
    };

    const typeLabelMap = {
      A: rowData?.typeName || t("company.a"),
      B: rowData?.typeName || t("company.b"),
      M: rowData?.typeName || t("company.m"),
    };

    const color = typeColorMap[rowData.type] || "var(--gray-400)";
    const label = typeLabelMap[rowData.type] || rowData?.typeName;

    if (!label) return null;

    return (
      <div className="user-avatar-button">
        <span style={{ color, fontSize: "1rem" }} className="contract-str">
          {label}
        </span>
      </div>
    );
  };

  const tagBodyTemplate = (rowData, props) => {
    if (!rowData.tagList || rowData.tagList.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {rowData.tagList.map((tagSeq) => {
          const tag = tagList.find((t) => t.companiesTagSeq === tagSeq);
          return tag ? (
            <span
              key={tagSeq}
              className="p-tag p-tag-rounded"
              style={{
                backgroundColor: "transparent",
                color: "#495057",
                border: "1px solid #6c757d",
                fontSize: "0.75rem",
                padding: "0.25rem 0.5rem",
              }}
            >
              {tag.tagName}
            </span>
          ) : null;
        })}
      </div>
    );
  };

  const rowsPerPageOptions = (dataLength) => {
    const options = [5, 10, 20, 50];
    let result = options.filter((opt) => opt <= dataLength);
    if (!result.includes(dataLength)) {
      result.push(dataLength);
    }
    return result;
  };

  const getSeriesByType = (seriesMap, statusKey, selectedType) => {
    const allSeries = seriesMap[statusKey] || [];
    return allSeries.find(s => s.label === selectedType);
  };

  const getTrendOptions = (data, selectedType) => {
    const seriesMap = data?.series || {};
    const categories = data?.categories || [];

    if (!data || !data.series || data.series.length === 0) {
      return {
        tooltip: {},
        xAxis: { type: "category", data: [] },
        yAxis: { type: "value" },
        series: [],
      };
    }

    // 響應式計算：根據數據量和螢幕尺寸動態調整顯示策略
    const dataLength = categories?.length || 0;
    const screenWidth = windowWidth || 1200;

    // 計算最佳的標籤顯示間隔
    let labelInterval;
    let labelRotation;
    let fontSize;

    if (screenWidth < 768) {
      // 手機螢幕
      labelInterval = dataLength > 5 ? Math.ceil(dataLength / 4) - 1 : 0;
      labelRotation = 90;
      fontSize = 10;
    } else if (screenWidth < 1024) {
      // 平板螢幕
      labelInterval = dataLength > 8 ? Math.ceil(dataLength / 6) - 1 : 0;
      labelRotation = 60;
      fontSize = 11;
    } else {
      // 桌面螢幕
      labelInterval = dataLength > 12 ? "auto" : 0;
      labelRotation = 45;
      fontSize = 11;
    }

    const unstartedSeries = getSeriesByType(seriesMap, "unstarted", selectedType);
    const activeSeries = getSeriesByType(seriesMap, "active", selectedType);
    const expiredSeries = getSeriesByType(seriesMap, "expired", selectedType);
    const netChangeSeries = getSeriesByType(seriesMap, "netChange", selectedType);
    // const cumulativeSeries = getSeriesByType(seriesMap, "cumulative", selectedType);

    const series = [];

    const barSeriesConfigs = [
      { key: unstartedSeries, name: t('unstarted') },
      { key: activeSeries, name: t('active') },
      { key: expiredSeries, name: t('expired') }
    ];

    barSeriesConfigs.forEach(config => {
      if (config.key) {
        series.push({
          ...config.key,
          name: config.name,
          type: 'bar',
          stack: 'total_status_stack',
          yAxisIndex: 1,
          data: config.key.data.map(Number),
          label: {
            show: true,
            position: 'insideTop',
            formatter: "{c}",
            fontSize: 12,
            color: '#fff',
            formatter: function (params) {
              return params.value === 0 ? '' : params.value;
            }
          },
        });
      }
    });

    if (netChangeSeries) {
      series.push({
        ...netChangeSeries,
        name: t('incrementalCount'),
        type: 'line',
        smooth: true,
        yAxisIndex: 0,
        data: netChangeSeries.data.map(Number),
        symbol: 'circle',
        symbolSize: 0,
        label: {
          show: true,
          position: 'top',
          formatter: "{c}",
          color: '#ff0000',
          formatter: function (params) {
            return params.value === 0 ? '' : params.value;
          }
        },
      });
    }

    return {
      tooltip: {
        trigger: "axis",
        confine: true,
        backgroundColor: "rgba(50, 50, 50, 0.9)",
        borderColor: "#333",
        borderWidth: 1,
        textStyle: {
          color: "#fff",
          fontSize: 12,
        },
        // 改善 tooltip 顯示完整的 X 軸標籤和數據
        formatter: function (params) {
          if (!params || params.length === 0) return "";

          let result = `<div style="margin-bottom: 8px; font-weight: bold; color: #fff; padding: 4px 0; border-bottom: 1px solid #666;">
                ${params[0].axisValue}
              </div>`;

          params.forEach((param) => {
            result += `<div style="margin: 4px 0; display: flex; align-items: center;">
                  <span style="display:inline-block; width:12px; height:12px; background-color:${param.color}; border-radius:50%; margin-right:10px; border: 2px solid #fff;"></span>
                  <span style="color: #fff;">
                    <strong>${param.seriesName}</strong>: ${param.value}
                  </span>
                </div>`;
          });

          return result;
        },
        axisPointer: {
          type: "cross",
          crossStyle: {
            color: "#999",
          },
          lineStyle: {
            color: "#999",
            width: 1,
            type: "dashed",
          },
        },
      },
      legend: {
        data: series.map(s => s.name),
        orient: "vertical",
        left: "left",
      },
      grid: {
        left: "10%",
        right: "10%",
        bottom: "20%", // 增加底部空間給 X 軸標籤
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          // 響應式智能顯示標籤間隔
          interval: labelInterval,
          rotate: labelRotation,
          fontSize: fontSize,
          color: "#666",
          margin: 15, // 增加標籤與軸線的間距
          // 智能格式化標籤
          formatter: function (value, index) {
            // 如果標籤太長，進行智能截斷和換行
            if (value.length > 12) {
              // 優先在空格處分割
              const words = value.split(" ");
              if (words.length > 1) {
                if (words.length <= 2) {
                  return words[0] + "\n" + words[1];
                } else {
                  const mid = Math.ceil(words.length / 2);
                  return words.slice(0, mid).join(" ") + "\n" + words.slice(mid).join(" ");
                }
              } else {
                // 單個長單詞的處理
                if (value.length > 16) {
                  return value.substring(0, 8) + "..\n" + value.substring(value.length - 8);
                } else {
                  const mid = Math.ceil(value.length / 2);
                  return value.substring(0, mid) + "\n" + value.substring(mid);
                }
              }
            }
            return value;
          },
          // 當標籤過多時，添加 hover 顯示完整標籤
          showMaxLabel: true,
          showMinLabel: true,
          rich: {
            // 定義文字樣式
            a: {
              fontSize: 11,
              lineHeight: 14,
            },
          },
        },
        axisTick: {
          alignWithLabel: true,
          length: 6,
        },
        // 增加軸線樣式
        axisLine: {
          lineStyle: {
            color: "#d0d7de",
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          name: t('incrementalCount'),
          // min: 0,
          minInterval: 1,
          position: 'left',
        },
        {
          type: 'value',
          name: t('cumulativeCount'),
          min: 0,
          minInterval: 1,
          position: 'right',
        },
      ],
      series: series,
      dataZoom: [{ type: 'slider' }, { type: 'inside' }],
    };
  };

  return (
    <>
      <Panel
        toggleable
        collapsed
        header={
          <>
            <div
              ref={buttonRef} // 觸發 OverlayPanel 的元素
              id="filterHeader"
              className="filter-header"
              style={{ lineHeight: "1.8", cursor: "pointer" }}
              onClick={(e) => op.current.toggle(e)} // 點擊時切換顯示
            >
              {displayText}
            </div>
            <OverlayPanel
              ref={op}
              target={buttonRef.current} // 指定要綁定的目標
              showCloseIcon
              style={{ maxWidth: "80%" }}
            >
              {tooltipText}
            </OverlayPanel>
          </>
        }
        collapseIcon="pi pi-sliders-h"
        expandIcon="pi pi-sliders-h"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid">
            {/* 身份別 */}
            <div className="col-3">
              <label htmlFor="typeList" className="form-label">
                {t("useType")}
              </label>
              <Controller
                name="typeList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={typeList}
                    optionLabel={"subitemName"}
                    optionValue={"subitemNo"}
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>

            {/* 狀態 */}
            <div className="col-3">
              <label htmlFor="statusList" className="form-label">
                {t("reviewStatusDistribution")}
              </label>
              <Controller
                name="statusList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={statusList}
                    optionLabel={"subitemName"}
                    optionValue={"subitemNo"}
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 授權類型 */}
            <div className="col-3">
              <label htmlFor="effectiveTypeList" className="form-label">
                {t("authorizationType")}
              </label>
              <Controller
                name="effectiveTypeList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={effectiveTypeList}
                    optionLabel={"subitemName"}
                    optionValue={"subitemNo"}
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 會員等級 */}
            <div className="col-3">
              <label htmlFor="membershipLevelList" className="form-label">
                {t("membershipLevel")}
              </label>
              <Controller
                name="membershipLevelList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={membershipLevelList}
                    optionLabel={"subitemName"}
                    optionValue={"subitemNo"}
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 使用語言 */}
            <div className="col-3">
              <label htmlFor="localeList" className="form-label">
                {t("useLanguage")}
              </label>
              <Controller
                name="localeList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={localeList}
                    optionLabel={"subitemName"}
                    optionValue={"subitemNo"}
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 行業別 */}
            <div className="col-3">
              <label htmlFor="industryList" className="form-label">
                {t("industry")}
              </label>
              <Controller
                name="industryList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={industryList}
                    optionLabel="industryName"
                    optionValue="industryCode"
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 國家 */}
            <div className="col-3">
              <label htmlFor="countryList" className="form-label">
                {t("country")}
              </label>
              <Controller
                name="countryList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={countryList}
                    optionLabel={"subitemName"}
                    optionValue={"subitemNo"}
                    maxSelectedLabels={3}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 標籤 */}
            <div className="col-3">
              <label htmlFor="tagList" className="form-label">
                {t("tag")}
              </label>
              <Controller
                name="tagList"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    filter
                    showClear
                    className="w-full"
                    style={{ width: "100%" }}
                    display="chip"
                    options={tagList}
                    optionLabel={"tagName"}
                    optionValue={"companiesTagSeq"}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.value);
                    }}
                  ></MultiSelect>
                )}
              />
            </div>
            {/* 授權起迄日 */}
            <div className="col-6">
              <label className="form-label">{t("authorizationDate")}</label>
              <div className="col-date-picker">
                <Controller
                  name="effectiveStartDate"
                  control={control}
                  render={({ field, fieldState, formState }) => (
                    <Calendar
                      dateFormat="yy-mm-dd"
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (e.value < watch("effectiveEndDate")) {
                          clearErrors("effectiveEndDate");
                        }
                      }}
                      showIcon
                    />
                  )}
                />
                {getFormErrorMessage(errors, "effectiveStartDate")}

                <Controller
                  name="effectiveEndDate"
                  control={control}
                  render={({ field, fieldState, formState }) => (
                    <Calendar
                      dateFormat="yy-mm-dd"
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (e.value > watch("effectiveStartDate")) {
                          clearErrors("effectiveStartDate");
                        }
                      }}
                      showIcon
                    />
                  )}
                />
                {getFormErrorMessage(errors, "effectiveEndDate")}
              </div>
            </div>
            {/* 建立起迄日 */}
            <div className="col-6">
              <label className="form-label">{t("createDateTo")}</label>
              <div className="col-date-picker">
                <Controller
                  name="createStarTime"
                  control={control}
                  render={({ field, fieldState, formState }) => (
                    <Calendar
                      dateFormat="yy-mm-dd"
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (e.value < watch("createEndTime")) {
                          clearErrors("createEndTime");
                        }
                      }}
                      showIcon
                    />
                  )}
                />
                {getFormErrorMessage(errors, "createStarTime")}

                <Controller
                  name="createEndTime"
                  control={control}
                  render={({ field, fieldState, formState }) => (
                    <Calendar
                      dateFormat="yy-mm-dd"
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (e.value > watch("createStarTime")) {
                          clearErrors("createStarTime");
                        }
                      }}
                      showIcon
                    />
                  )}
                />
                {getFormErrorMessage(errors, "createEndTime")}
              </div>
            </div>
          </div>
          <div className="col-12 gap-2 flex-all-center">
            <Button type="submit" icon="pi pi-search" label={t("button.query")} />
            <Button type="button" label={t("button.reset")} icon="pi pi-refresh" onClick={() => reset(defaultValues)} />
          </div>
        </form>
      </Panel>
      {/* 統計卡片區域標題和全域切換按鈕 */}
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="m-0"></div>
        <div className="flex align-items-center gap-2">
          <span className="text-sm text-600">{t("viewMode")}:</span>
          <Button
            tooltipOptions={{ position: "left" }}
            icon={globalViewMode ? "pi pi-table" : "pi pi-chart-pie"}
            label={globalViewMode ? t("statisticsTable") : t("SummaryGraph")}
            size="small"
            severity="secondary"
            outlined
            onClick={() => setGlobalViewMode(!globalViewMode)}
          />
        </div>
      </div>

      <div className="dashboard-grid">
        {/* 註冊會員身份別 */}
        <CusChart
          title={t("companyRoleStatusDistribution")}
          dataList={totalCompanyList}
          charType="pie"
          globalViewMode={globalViewMode}
        />

        {/* 審核狀態 */}
        <CusChart
          title={t("reviewStatusDistribution")}
          dataList={totalStatusList}
          charType="pie"
          globalViewMode={globalViewMode}
        />

        {/* 授權類型(正式 試用) */}
        <CusChart
          title={t("authorizationType")}
          dataList={totalEffectiveTypeList}
          charType="pie"
          globalViewMode={globalViewMode}
        />

        {/* 會員等級 */}
        <CusChart
          title={t("membershipLevel")}
          dataList={totalMembershipLevelList}
          charType="pie"
          globalViewMode={globalViewMode}
        />

        {/* 帳戶類型分佈 */}
        <CusChart
          title={t("userRoleStatusDistribution")}
          dataList={totalUserList}
          charType="pie"
          globalViewMode={globalViewMode}
        />

        {/* 行業別 */}
        <CusChart
          title={t("industryDistribution")}
          dataList={totalIndustryList}
          charType="bar"
          globalViewMode={globalViewMode}
        />

        {/* 國家分佈 */}
        <CusChart
          title={t("countryDistribution")}
          dataList={totalCountryList}
          charType="bar"
          globalViewMode={globalViewMode}
        />

        {/* 語系 */}
        <CusChart
          title={t("localeDistribution")}
          dataList={totalLocaleList}
          charType="bar"
          globalViewMode={globalViewMode}
        />

        {/* 語系 */}
        <CusChart title={t("tag")} dataList={totalTagList} charType="bar" globalViewMode={globalViewMode} />
      </div>

      {/* 註冊趨勢 */}
      <div className="p-card">
        <div className="p-card-body">
          <div className="p-card-title grid">
            <div className="col-8">
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: "4px" }}>
                {t("registrationTrend")}
              </span>
            </div>

            {/* 期間 */}
            <div className="col-2">
              <Dropdown
                optionLabel="label"
                optionValue="value"
                value={periodType}
                virtualScrollerOptions={{ itemSize: 40 }}
                placeholder={t("plz_chs")}
                className={"w-full"}
                options={[
                  { label: t("week"), value: "week" },
                  { label: t("month"), value: "month" },
                ]}
                onChange={(event) => {
                  setPeriodType(event.value);
                  setValue("periodType", event.value);
                  axios_getDashboardData({
                    typeList: getValues("typeList"),
                    statusList: getValues("statusList"),
                    effectiveTypeList: getValues("effectiveTypeList"),
                    membershipLevelList: getValues("membershipLevelList"),
                    countryList: getValues("countryList"),
                    localeList: getValues("localeList"),
                    effectiveStartDate: getValues("effectiveStartDate")
                      ? setformate(getValues("effectiveStartDate"), "YYYY/MM/DD")
                      : "",
                    effectiveEndDate: getValues("effectiveEndDate")
                      ? setformate(getValues("effectiveEndDate"), "YYYY/MM/DD")
                      : "",
                    createStarTime: getValues("createStarTime")
                      ? setformate(getValues("createStarTime"), "YYYY/MM/DD")
                      : "",
                    createEndTime: getValues("createEndTime")
                      ? setformate(getValues("createEndTime"), "YYYY/MM/DD")
                      : "",
                    periodType: event.value,
                  });
                }}
              />
            </div>

            {/* 公司種類 */}
            <div className="col-2">
              <Dropdown
                optionLabel="label"
                optionValue="value"
                value={selectedType}
                options={typeOptions}
                onChange={(e) => setSelectedType(e.value)}
                placeholder={t("useType")}
                className={"w-full"}
              />
            </div>


            <div
              className="dashboard-table-wrapper"
              style={{
                padding: "10px",
                overflow: "hidden",
                minHeight: "400px",
              }}
            >

              <ReactECharts
                key={`combined-trend-${selectedType}-${windowWidth}`}
                option={getTrendOptions(registrationTrend, selectedType)}
                style={{
                  height: "450px",
                  width: "100%",
                  minHeight: "350px",
                  maxWidth: "100%",
                }}
                opts={{
                  renderer: "canvas",
                  devicePixelRatio: 2, // 改善高解析度螢幕的顯示品質
                }}
                notMerge={true}
                lazyUpdate={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 總表 */}
      <div className="p-card">
        <div className="p-card-body">
          <div className="p-card-title grid">
            <div className="col-10">
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: "4px" }}>{`${t(
                "summaryTable"
              )}`}</span>
            </div>
          </div>
        </div>
        <DataTable
          scrollable
          stripedRows
          resizableColumns
          className="white-data-table hover-highlight no-shadow"
          value={countdownList}
          paginator
          rows={5}
          pageLinkSize={3}
          rowsPerPageOptions={rowsPerPageOptions(countdownList.length)}
          sortField="countdown"
          sortOrder={1}
          paginatorTemplate={{
            layout:
              rowsPerPageOptions(countdownList.length).length > 1
                ? "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport "
                : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
            RowsPerPageDropdown: (option) => (
              <>
                {t("rowsPerPage")}
                {option.element}
              </>
            ),
          }}
          currentPageReportTemplate={t("pagination_report")}
          emptyMessage={<NoInfo panel="table" />}
          filterDisplay="menu"
          filters={filters}
        >
          <Column
            field="companyName"
            header={t("companyName")}
            frozen
            alignFrozen="left"
            style={{
              width: "300px",
              minWidth: "300px",
              maxWidth: "300px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          />
          <Column field="typeName" header={t("type")} body={companyTypeNameTemplate} />
          <Column field="status" header={t("status")} body={statusBodyTemplate} />
          <Column field="effectiveType" header={t("authorizationType")} body={companyLevelNameTemplate} />
          <Column field="membershipLevelName" header={t("membershipLevel")} />
          <Column header={t("tag")} body={tagBodyTemplate} />
          <Column field="countryName" header={t("country")} />
          <Column field="localeName" header={t("useLanguage")} />
          <Column
            header={t("authorizationDate")}
            body={(rowData) => {
              const start = rowData.effectiveStartDate ?? "";
              const end = rowData.effectiveEndDate ?? "";
              if (!start && !end) return null;
              return `${start}${start && end ? " ~ " : ""}${end}`;
            }}
          />
          <Column
            field="countdown"
            header={t("countdown")}
            frozen
            alignFrozen="right"
            filter
            dataType="numeric"
            showFilterMenuOptions={false}
            showApplyButton={false}
            filterElement={(options) => (
              <Dropdown
                value={options.value}
                placeholder={t("plz_chs")}
                options={[
                  { label: t("expired"), value: -1 },
                  { label: "90", value: 90 },
                  { label: "30", value: 30 },
                  { label: "14", value: 14 },
                ]}
                onChange={(e) => options.filterApplyCallback(e.value)}
                className="p-column-filter"
              />
            )}
            sortable
            sortFunction={(e) => {
              const order = e.order;
              const rank = (row) => {
                if (row.countdown === "-1") return -Infinity;
                if (row.countdown == null) return Infinity;
                return Number(row.rawCountdown ?? row.countdown);
              };

              return [...e.data].sort((a, b) => (rank(a) - rank(b)) * order);
            }}
            body={(rowData) => {
              if (rowData.countdown === "-1") {
                return <span className={`p-tag p-tag-danger`}>{t("expired")}</span>;
              } else if (rowData.countdown == null) {
                return null;
              } else {
                return `${rowData.countdown}`;
              }
            }}
          />
        </DataTable>
      </div>
    </>
  );
};
export default UserDashboardIndex;
